import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertCircle, RefreshCw, Sparkles, RotateCcw, Copy, Check } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export class ErrorBoundary extends (React.Component as unknown as {
  new (props: Props): {
    props: Props;
    state: State;
    setState: (update: Partial<State> | ((prev: State) => Partial<State>)) => void;
  };
}) {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary caught error]:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRecover = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.clear();
        localStorage.removeItem('current_active_web_code');
        localStorage.removeItem('couple_last_viewed_tab');
      }
    } catch {}
    window.location.href = '/';
  };

  handleCopyDetails = () => {
    const details = [
      this.state.error?.message,
      this.state.error?.stack,
      this.state.errorInfo?.componentStack,
    ]
      .filter(Boolean)
      .join('\n\n');

    if (navigator.clipboard) {
      navigator.clipboard.writeText(details).catch(() => {});
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Error inesperado';
      const errorStack = this.state.error?.stack || '';
      const compStack = this.state.errorInfo?.componentStack || '';

      return (
        <div className="min-h-screen w-full bg-[#faf9f8] text-stone-900 flex flex-col items-center justify-center p-4 sm:p-6 select-none font-sans">
          <div className="max-w-md w-full bg-white rounded-3xl p-6 sm:p-8 border border-rose-100 shadow-xl shadow-rose-500/5 text-center flex flex-col items-center space-y-4 animate-fade-in">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-rose-50 border border-rose-200/60 flex items-center justify-center text-rose-500 shadow-sm shrink-0">
              <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-rose-500" />
            </div>

            <div className="space-y-1.5">
              <h1 className="text-xl sm:text-2xl font-bold font-['Playfair_Display',serif] text-stone-900 tracking-tight">
                Nuestros Recuerdos
              </h1>
              <p className="text-xs sm:text-sm font-medium text-stone-600">
                La aplicación tuvo un contratiempo al cargar este módulo.
              </p>
              <p className="text-[11px] sm:text-xs text-stone-400 leading-relaxed">
                Tus recuerdos y cartas están completamente seguros en la nube.
              </p>
            </div>

            <div className="w-full bg-stone-50 rounded-2xl p-3.5 border border-stone-200/60 text-left space-y-2">
              <div className="flex items-center justify-between text-rose-600 text-xs font-semibold">
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>Detalle técnico</span>
                </div>
                <button
                  type="button"
                  onClick={this.handleCopyDetails}
                  className="px-2 py-0.5 rounded-md bg-stone-200/70 hover:bg-stone-300 text-stone-700 text-[10px] flex items-center gap-1 cursor-pointer transition-colors"
                >
                  {this.state.copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  <span>{this.state.copied ? 'Copiado' : 'Copiar'}</span>
                </button>
              </div>
              <p className="text-[11px] font-mono text-stone-700 break-all leading-tight font-semibold">
                {errorMessage}
              </p>
              {(errorStack || compStack) && (
                <details className="mt-1 text-[10px] text-stone-500 font-mono">
                  <summary className="cursor-pointer text-stone-600 hover:text-stone-900 select-none pb-1">
                    Ver traza completa
                  </summary>
                  <div className="max-h-36 overflow-y-auto bg-white p-2 rounded-lg border border-stone-200 whitespace-pre-wrap break-all leading-tight mt-1">
                    {errorStack || compStack}
                  </div>
                </details>
              )}
            </div>

            <div className="w-full flex flex-col sm:flex-row gap-2 pt-1">
              <button
                type="button"
                onClick={this.handleRecover}
                className="py-3 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/20 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-95"
              >
                <Sparkles className="w-4 h-4" />
                <span>Reintentar</span>
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="flex-1 py-3 px-4 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Recargar aplicación</span>
              </button>
              <button
                type="button"
                onClick={this.handleClearAndReload}
                className="py-3 px-3.5 bg-stone-100 hover:bg-stone-200 text-stone-600 font-medium text-xs rounded-xl flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-95"
                title="Limpiar sesión y volver al inicio"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Inicio</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
